import {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {useDispatch} from "react-redux";
import {Lock} from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "../../../basic/alert-dialog";
import {Card} from "../../../basic/card";
import {Button} from "../../../basic/button";
import {Input} from "../../../basic/input";
import {Separator} from "../../../basic/separator";
import {Form, FormControl, FormField, FormItem, FormLabel} from "../../../basic/form";
import {Alert, AlertDescription} from "../../../basic/alert";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useGetPortalTeamQuery, useUpdatePortalTeamMutation} from "../../../../store/generated/api.ts";
import {toast} from "sonner";
import {setTeamUpdatePending} from "../../../../store/updatesSlice";

const DEFAULT_VALUES = {
    semester1Start: "",
    semester1End: "",
    semester2Start: "",
    semester2End: "",
};

type EducationalFormValues = {
    semester1Start: string;
    semester1End: string;
    semester2Start: string;
    semester2End: string;
};

export function EducationalSettings() {
    const {t} = useTranslationWithKeys();
    const dispatch = useDispatch();
    const {data, isLoading, error, refetch} = useGetPortalTeamQuery({
        page: 0,
        size: 10
    });
    const team = data?.team;
    const educationalSettings = team?.educationalSettings;
    const isLocked = educationalSettings?.settingsLocked === true;

    const [updatePortalTeam, {isLoading: isUpdating}] = useUpdatePortalTeamMutation();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const form = useForm<EducationalFormValues>({
        defaultValues: DEFAULT_VALUES,
    });

    const {reset, formState: {isDirty}} = form;

    useEffect(() => {
        if (team) {
            const starts = educationalSettings?.semesterStartDates;
            const ends = educationalSettings?.semesterEndDates;
            reset({
                semester1Start: starts?.[0] ?? "",
                semester1End: ends?.[0] ?? "",
                semester2Start: starts?.[1] ?? "",
                semester2End: ends?.[1] ?? "",
            });
        }
    }, [team, educationalSettings, reset]);

    const onSubmit = async (values: EducationalFormValues) => {
        setConfirmOpen(false);
        try {
            dispatch(setTeamUpdatePending(true));

            await updatePortalTeam({
                vwpTeamUpdateRequest: {
                    educationalSettings: {
                        semesterStartDates: [values.semester1Start, values.semester2Start],
                        semesterEndDates: [values.semester1End, values.semester2End],
                    }
                }
            }).unwrap();

            toast.success(t('settings.teamSettingsSaved'));
            await refetch();
        } catch {
            toast.error(t('error.generic', {message: t('common.error')}));
        } finally {
            dispatch(setTeamUpdatePending(false));
        }
    };

    if (isLoading) {
        return (
            <Card className="p-6 shadow-sm border-border/50">
                <div className="flex items-center justify-center h-32">
                    <p className="text-muted-foreground">{t('common.loading')}</p>
                </div>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="p-6 shadow-sm border-border/50">
                <div className="flex items-center justify-center h-32">
                    <p className="text-destructive">{t('settings.loadTeamError')}</p>
                </div>
            </Card>
        );
    }

    return (
        <Card className="p-6 shadow-sm border-border/50">
            <h3 className="mb-2">{t('settings.educational.title')}</h3>
            <p className="text-muted-foreground text-sm mb-4">{t('settings.educational.description')}</p>
            {isLocked && (
                <Alert className="mb-4">
                    <Lock className="h-4 w-4"/>
                    <AlertDescription>{t('settings.educational.lockedMessage')}</AlertDescription>
                </Alert>
            )}
            <Form {...form}>
                <form onSubmit={(e) => { e.preventDefault(); setConfirmOpen(true); }} className="space-y-6">
                    <div className="space-y-4">
                        <h4 className="font-medium">{t('settings.educational.semester1')}</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="semester1Start"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>{t('settings.educational.startDate')}</FormLabel>
                                        <FormControl>
                                            <Input type="date" disabled={isLocked} {...field} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="semester1End"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>{t('settings.educational.endDate')}</FormLabel>
                                        <FormControl>
                                            <Input type="date" disabled={isLocked} {...field} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className="space-y-4">
                        <h4 className="font-medium">{t('settings.educational.semester2')}</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="semester2Start"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>{t('settings.educational.startDate')}</FormLabel>
                                        <FormControl>
                                            <Input type="date" disabled={isLocked} {...field} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="semester2End"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>{t('settings.educational.endDate')}</FormLabel>
                                        <FormControl>
                                            <Input type="date" disabled={isLocked} {...field} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <Separator className="my-6"/>
                    <div className="flex justify-end">
                        <Button
                            type="submit"
                            disabled={!isDirty || isUpdating || isLocked}
                            className="rounded-full px-8"
                        >
                            {isUpdating ? t('common.action.saving') : t('settings.saveChanges')}
                        </Button>
                    </div>
                </form>
            </Form>
            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('settings.educational.confirmTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('settings.educational.confirmDescription')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isUpdating}>
                            {t('common.action.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={form.handleSubmit(onSubmit)} disabled={isUpdating}>
                            {t('settings.educational.confirmSave')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}
